import { useMemo } from 'react';
import type { RiskHazard } from '@/types';

export type RiskLevelBand = { code?: string; name: string; hex: string; min: number; max: number };

/** Pasma z konfiguracji (meta.riskLevels); fallback = domyślna macierz liniowa 1–25. */
export const FALLBACK_BANDS: RiskLevelBand[] = [
  { name: 'Niskie', hex: '#97C459', min: 1, max: 4 },
  { name: 'Średnie', hex: '#EF9F27', min: 5, max: 9 },
  { name: 'Wysokie', hex: '#E24B4A', min: 10, max: 15 },
  { name: 'Krytyczne', hex: '#791F1F', min: 16, max: 25 },
];

export function riskLevel(score: number, levels?: RiskLevelBand[] | null): { name: string; hex: string } {
  const bands = levels && levels.length ? levels : FALLBACK_BANDS;
  for (const b of bands) if (score <= b.max) return b;
  return bands[bands.length - 1];
}

/** Wynik ryzyka wg skonfigurowanej skali dotkliwości (F5): LINIOWA=P×D, KWADRATOWA=P×D². */
export function riskScore(p: number, d: number, severityScale?: string | null): number {
  return severityScale === 'KWADRATOWA' ? p * d * d : p * d;
}

/**
 * Macierz ryzyka 5×5 (prawdopodobieństwo × dotkliwość). Komórki kolorowane wg poziomu ryzyka,
 * z liczbą zagrożeń wpadających w daną komórkę. Odzwierciedla PDF (spójność ekran ↔ dokument).
 */
export function RiskMatrix({ hazards, mode = 'residual', levels, severityScale }: {
  hazards: RiskHazard[]; mode?: 'initial' | 'residual';
  levels?: RiskLevelBand[] | null; severityScale?: string | null;
}) {
  const counts = useMemo(() => {
    const m: Record<string, RiskHazard[]> = {};
    for (const h of hazards) {
      const p = mode === 'residual' ? h.probabilityResidual : h.probabilityInitial;
      const s = mode === 'residual' ? h.severityResidual : h.severityInitial;
      const key = `${s}-${p}`;
      (m[key] ||= []).push(h);
    }
    return m;
  }, [hazards, mode]);

  return (
    <div className="inline-block">
      <div className="flex gap-1.5">
        <div className="flex flex-col justify-between items-end py-0.5 text-[10px] text-ink-400 w-4">
          {[5, 4, 3, 2, 1].map(d => <span key={d}>{d}</span>)}
        </div>
        <div>
          <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(5, 34px)', gridTemplateRows: 'repeat(5, 30px)' }}>
            {[5, 4, 3, 2, 1].map(sev =>
              [1, 2, 3, 4, 5].map(prob => {
                const score = riskScore(prob, sev, severityScale);
                const hs = counts[`${sev}-${prob}`] || [];
                const lvl = riskLevel(score, levels);
                // przy skali kwadratowej równanie w dymku musi pokazywać ważony czynnik (P×D²), nie fałszywe P×D
                const eq = severityScale === 'KWADRATOWA' ? `P=${prob} × D²=${sev * sev}` : `P=${prob} × D=${sev}`;
                return (
                  <div
                    key={`${sev}-${prob}`}
                    title={`${eq} = ${score} (${lvl.name})${hs.length ? ` · ${hs.length} zagrożeń` : ''}`}
                    className="rounded flex items-center justify-center text-[12px] font-bold text-white"
                    style={{ background: lvl.hex, opacity: hs.length ? 1 : 0.28 }}
                  >
                    {hs.length ? hs.length : ''}
                  </div>
                );
              })
            )}
          </div>
          <div className="grid gap-1 mt-1 text-center text-[10px] text-ink-400" style={{ gridTemplateColumns: 'repeat(5, 34px)' }}>
            {[1, 2, 3, 4, 5].map(p => <span key={p}>{p}</span>)}
          </div>
          <div className="text-[10px] text-ink-400 mt-1">Prawdopodobieństwo →</div>
        </div>
      </div>
    </div>
  );
}
